import { getAuth } from 'firebase/auth';

const NOTIFY_FN = '/.netlify/functions/notify';

/**
 * Atomically make this account the only server owner of one origin-level FCM
 * token. The notify Function uses Admin authority to remove the token from a
 * prior account before attaching it to the authenticated caller.
 */
export function createPushClaimId(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `claim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export async function claimPushTokenOwnership(
  token: string,
  explicitOptIn: boolean,
  requestedClaimId = createPushClaimId(),
  isCurrentOperation: () => boolean = () => true,
): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user || !isCurrentOperation()) return null;
  const claimId = requestedClaimId;
  const idToken = await user.getIdToken();
  if (getAuth().currentUser !== user || !isCurrentOperation()) return null;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  };
  const claim = { action: 'claimPushToken', pushToken: token, claimId, explicitOptIn };
  // Read an opaque predecessor, then compare it inside the claim transaction.
  // A request already in flight cannot undo a newer claim even when its HTTP
  // arrival order differs from the order in which the user tapped reconnect.
  const inspection = await fetch(NOTIFY_FN, {
    ...options, body: JSON.stringify({ ...claim, inspectOnly: true }),
  });
  const inspected = await inspection.json().catch(() => null) as {
    ok?: boolean; ownerVersion?: string; code?: string;
  } | null;
  if (getAuth().currentUser !== user || !isCurrentOperation()) return null;
  if (inspection.status === 409 && inspected?.code === 'push-token-rejected') {
    throw Object.assign(new Error('This notification registration is no longer valid.'), {
      code: 'push-token-rejected',
    });
  }
  if (!inspection.ok || inspected?.ok !== true || typeof inspected.ownerVersion !== 'string'
    || (inspected.ownerVersion !== 'unclaimed' && !/^[a-f0-9]{64}$/.test(inspected.ownerVersion))) return null;
  const response = await fetch(NOTIFY_FN, {
    ...options, body: JSON.stringify({ ...claim, expectedOwnerVersion: inspected.ownerVersion }),
  });
  if (!response.ok) {
    const failure = await response.json().catch(() => null) as { code?: string } | null;
    if (response.status === 409 && failure?.code === 'push-token-rejected') {
      throw Object.assign(new Error('This notification registration is no longer valid.'), {
        code: 'push-token-rejected',
      });
    }
    return null;
  }
  const result = await response.json().catch(() => null) as { ok?: boolean; uid?: string; claimId?: string } | null;
  return result?.ok === true && result.uid === user.uid && result.claimId === claimId
    ? claimId
    : null;
}

/**
 * Remove one origin-level token from every Hub account after an account
 * boundary changes underneath an enrollment. Knowing the exact FCM token is
 * already sufficient to claim it through the endpoint above, so this does
 * not grant a broader capability. It only gives the browser a fail-closed
 * cleanup path when Firebase cannot delete the provider token.
 */
export async function releasePushTokenOwnership(
  token: string,
  expectedClaimId: string | null,
  allowUnclaimed = false,
): Promise<boolean> {
  const user = getAuth().currentUser;
  if (!user) return false;
  const idToken = await user.getIdToken();
  const response = await fetch(NOTIFY_FN, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      action: 'releasePushToken',
      pushToken: token,
      expectedClaimId,
      ...(allowUnclaimed ? { allowUnclaimed: true } : {}),
    }),
  });
  if (!response.ok) return false;
  const result = await response.json().catch(() => null) as { ok?: boolean } | null;
  return result?.ok === true;
}
